import { useEffect, useMemo, useRef, useState } from 'react';
import { Map, AdvancedMarker, InfoWindow, useMap } from '@vis.gl/react-google-maps';
import { InformationCircleIcon } from '@heroicons/react/24/outline';
import { WfWorkplace } from 'src/api/wf/models/WfWorkplace';
import { WfUser } from 'src/api/wf/models/WfUser';
import { MapIconType } from './pin/MapPin';
import {
  getOperationStatusTranslationKey,
  getWfWorkplaceMarkerZIndex,
} from 'src/api/wf/models/enums/WfOperationtStatus';
import WF_WorkplaceMarker from './marker/WF_WorkplaceMarker';
import MapLegendOverlay from 'src/ui/wf/common/map/MapLegendOverlay';
import WF_LocationMarker from './marker/WF_LocationMarker';
import { useWfFilterStore } from 'src/api/wf/stores/WfFilterStore';
import {
  defaultMapCenter,
  defaultMapSearchRange,
  defaultMapZoom,
} from 'src/common/location/defaultMapSettings';
import useWfUserService from 'src/api/wf/service/WfUserService';
import MapPortal from './MapPortal';
import { useAlertStore } from 'src/ui/common/alert/AlertStore';
import { useWfUserStore } from 'src/api/wf/stores/WfUserStore';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useUserAgent } from 'src/common/userAgent/useUserAgent';
import { useWfAlertHistoryStore } from 'src/api/wf/stores/WfAlertHistoryStore';
import { useWfWorkplaceStore } from 'src/api/wf/stores/WfWorkplaceStore';
import useWfWorkplaceService from 'src/api/wf/service/WfWorkplaceService';
import { useWfLocationService } from 'src/api/wf/service/WfLocationService';
import { useNotifyMap } from 'src/ui/common/alert/useNotifyMap';
import { wfNavigate } from 'src/ui/wf/router/WfScreenRegistration';
import { WfScreensMap } from 'src/ui/wf/router/WfScreensMap';
import { WF_Screen } from 'src/ui/wf/router/WfScreens';
import Button from '../button/Button';
import LinkButton from '../button/LinkButton';

export type PopupChangedEvent = { userId?: string; siteId?: string };

export type MapProps = {
  workplaces: WfWorkplace[];
  workers: WfUser[];
  user: WfUser | null | undefined;
  className?: string;
  legendButtonVisible?: boolean;
  popupOpened?: (event: PopupChangedEvent) => void;
  popupClosed?: (event: PopupChangedEvent) => void;
  closeAllPopups?: (fn: () => void) => void;
  recenterTrigger?: number;
};

const MapCenterUpdater = ({
  center,
  recenterTrigger,
}: {
  center: { lat: number; lng: number };
  recenterTrigger: number | undefined;
}) => {
  const map = useMap();
  const prevCenterRef = useRef<{ lat: number; lng: number } | null>(null);
  const prevTriggerRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (map && center) {
      const prev = prevCenterRef.current;
      const triggerChanged = recenterTrigger !== prevTriggerRef.current;
      if (triggerChanged || !prev || prev.lat !== center.lat || prev.lng !== center.lng) {
        map.panTo(center);
        prevCenterRef.current = { lat: center.lat, lng: center.lng };
        prevTriggerRef.current = recenterTrigger;
      }
    }
  }, [map, center, recenterTrigger]);
  return null;
};

const MapZoomUpdater = ({
  zoom,
  recenterTrigger,
}: {
  zoom: number;
  recenterTrigger: number | undefined;
}) => {
  const map = useMap();
  const prevTriggerRef = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (map && recenterTrigger !== prevTriggerRef.current) {
      map.setZoom(zoom);
      prevTriggerRef.current = recenterTrigger;
    }
  }, [map, zoom, recenterTrigger]);
  return null;
};

const WF_Map = ({
  workplaces,
  workers,
  user,
  className,
  legendButtonVisible,
  popupOpened,
  popupClosed,
  recenterTrigger,
}: MapProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userAgent = useUserAgent();
  const [showOverlay, setShowOverlay] = useState(false);
  const [activeCustomerId, setActiveCustomerId] = useState<string | null>(null);
  const { filterParameters, salesOfficeOptions, sectionOptions, mapCenter, userOffice, zoom } =
    useWfFilterStore();
  const { wfGetUsers } = useWfUserService();
  const { mapPortalContent, hideAlert } = useAlertStore();
  const { setUsers } = useWfUserStore();
  const { patchHistorySearchParams } = useWfAlertHistoryStore();
  const { setSelectedWorkplace } = useWfWorkplaceStore();
  const { createWorkplaceReservation } = useWfWorkplaceService();
  const { updateWfUserLocation } = useWfLocationService();
  const notifyMap = useNotifyMap();

  const currentCenter = mapCenter
    ? { lat: mapCenter.latitude, lng: mapCenter.longitude }
    : { lat: defaultMapCenter.latitude, lng: defaultMapCenter.longitude };

  const activeSite = useMemo(
    () => workplaces.find((site) => site.customerId === activeCustomerId),
    [workplaces, activeCustomerId]
  );

  const isActiveSiteReserved = (activeSite?.details?.reservations?.length ?? 0) !== 0;

  const handleOpenWorkplaceDetails = () => {
    if (!activeSite) return;
    setSelectedWorkplace(activeSite);
    wfNavigate(navigate, WfScreensMap[WF_Screen.WorkplaceDetails].path);
  };

  const handleOpenAlertHistory = () => {
    if (!activeSite) return;
    patchHistorySearchParams({
      buildingName: activeSite.name,
      branchCode: activeSite.branchCode,
      salesOfficeCode: activeSite.salesOfficeCode,
      organizationCode: activeSite.organizationCode,
      customerNumber: activeSite.customerId.toString(),
    });
    wfNavigate(navigate, WfScreensMap[WF_Screen.AlertHistory].path);
  };

  const handleSelection = () => {
    if (!activeSite) return;
    setSelectedWorkplace(activeSite);
  };

  const handleReservation = async () => {
    setActiveCustomerId(null);
    if (!activeSite || !user) return;
    const reservationParams = {
      engineerUserId: user.id,
      customerId: activeSite.customerId,
      branchCode: activeSite.branchCode,
    };
    try {
      const response = await createWorkplaceReservation(reservationParams);
      if (response) {
        notifyMap('wf.searchHeader.reservationSuccess');
        if (userAgent.isMobile) {
          updateWfUserLocation();
        }
      } else {
        notifyMap('wf.searchHeader.reservationFailed');
      }
    } catch (error) {
      console.error('Error with canceling or fetching updated data:', error);
      notifyMap('wf.searchHeader.reservationFailed');
    }
  };

  const markers = useMemo(
    () =>
      workplaces
        .filter(
          (site): site is WfWorkplace & { location: NonNullable<WfWorkplace['location']> } =>
            site.location != null
        )
        .map((site) => (
          <AdvancedMarker
            key={site.customerId}
            clickable
            position={{ lat: site.location.latitude, lng: site.location.longitude }}
            zIndex={getWfWorkplaceMarkerZIndex(site.details?.statusId)}
            onClick={() => {
              popupOpened?.({ siteId: site.customerId });
              setActiveCustomerId(site.customerId);
            }}
          >
            <WF_WorkplaceMarker site={site} canOpenPopup={false} />
          </AdvancedMarker>
        )),
    [workplaces, popupOpened]
  );

  useEffect(() => {
    const selectedSection = sectionOptions.find(
      (section) => section.code === filterParameters.section
    );
    const selectedSalesOffice = salesOfficeOptions.find(
      (office) => office.code === filterParameters.salesOffice
    );
    const searchRange =
      selectedSection?.searchRange ?? selectedSalesOffice?.searchRange ?? defaultMapSearchRange;
    const location = selectedSection?.location ?? selectedSalesOffice?.location;

    if (location) {
      wfGetUsers(location.latitude, location.longitude, searchRange);
    } else {
      setUsers([]);
    }
  }, [
    wfGetUsers,
    filterParameters.salesOffice,
    filterParameters.section,
    salesOfficeOptions,
    sectionOptions,
    setUsers,
  ]);

  useEffect(() => {
    if (!mapPortalContent) return;
    const handleScreenClick = () => {
      hideAlert();
    };

    document.addEventListener('mousedown', handleScreenClick);
    document.addEventListener('touchstart', handleScreenClick);

    return () => {
      document.removeEventListener('mousedown', handleScreenClick);
      document.removeEventListener('touchstart', handleScreenClick);
    };
  }, [hideAlert, mapPortalContent]);

  return (
    <div className={`relative size-full ${className ?? ''}`}>
      <Map
        defaultCenter={currentCenter}
        defaultZoom={zoom ?? defaultMapZoom}
        mapId="WF_MAP_ID"
        disableDefaultUI={false}
        gestureHandling={'greedy'}
        className="size-full"
      >
        <MapPortal>
          <div className="pointer-events-none fixed inset-0 z-[9999] flex items-center justify-center">
            {mapPortalContent && mapPortalContent}
          </div>
        </MapPortal>
        <MapCenterUpdater center={currentCenter} recenterTrigger={recenterTrigger} />
        <MapZoomUpdater zoom={zoom ?? defaultMapZoom} recenterTrigger={recenterTrigger} />
        {markers}
        {activeSite && activeSite.location && (
          <InfoWindow
            position={{
              lat: activeSite.location.latitude,
              lng: activeSite.location.longitude,
            }}
            onCloseClick={() => {
              setActiveCustomerId(null);
              popupClosed?.({ siteId: activeSite.customerId });
            }}
          >
            <div className="flex flex-col gap-2 p-1 text-sm text-black">
              <LinkButton
                title={activeSite.customerId.toString()}
                onClick={handleOpenWorkplaceDetails}
              />
              <label className="m-0 text-center font-medium">{`${
                activeSite.emergencyClass ? '★ ' : ''
              }${activeSite.name}`}</label>
              <label className="m-0 text-center font-bold">
                {`${t(getOperationStatusTranslationKey(activeSite.details?.statusId))}`}
              </label>
              <Button
                className="rounded border border-blue-600 px-3 py-1 text-blue-600 transition hover:bg-blue-600 hover:text-white"
                title={t('wf.markers.history')}
                onClick={handleOpenAlertHistory}
              />
              {userAgent.isMobile && !isActiveSiteReserved && (
                <Button
                  title={t('wf.markers.reserve')}
                  onClick={handleReservation}
                  disabled={isActiveSiteReserved}
                />
              )}
              {userAgent.isDesktop && (
                <Button title={t('wf.markers.select')} onClick={handleSelection} />
              )}
            </div>
          </InfoWindow>
        )}
        {user?.location && (
          <AdvancedMarker
            position={{ lat: user.location.latitude, lng: user.location.longitude }}
            clickable
            onClick={() => popupOpened?.({ userId: user.id })}
          >
            <WF_LocationMarker
              type={
                user?.salesOfficeCode === filterParameters.salesOffice
                  ? MapIconType.currentUserLocation
                  : MapIconType.worker
              }
              location={user.location}
              user={user}
              canOpenPopup={true}
              popupClosed={() => popupClosed?.({ userId: user.id })}
            />
          </AdvancedMarker>
        )}
        {userOffice?.location && (
          <AdvancedMarker
            clickable
            position={{ lat: userOffice.location.latitude, lng: userOffice.location.longitude }}
          >
            <WF_LocationMarker
              type={MapIconType.office}
              location={{
                latitude: userOffice.location.latitude,
                longitude: userOffice.location.longitude,
              }}
              canOpenPopup={false}
            />
          </AdvancedMarker>
        )}
        {workers
          .filter(
            (worker): worker is WfUser & { location: NonNullable<WfUser['location']> } =>
              worker.id !== user?.id && worker.location != null
          )
          .map((worker) => (
            <AdvancedMarker
              key={worker.id}
              clickable
              position={{ lat: worker.location.latitude, lng: worker.location.longitude }}
              onClick={() => popupOpened?.({ userId: worker.id })}
            >
              <WF_LocationMarker
                type={
                  worker.salesOfficeCode === filterParameters.salesOffice
                    ? MapIconType.workerMatch
                    : MapIconType.worker
                }
                location={worker.location}
                user={worker}
                canOpenPopup={true}
                popupClosed={() => popupClosed?.({ userId: worker.id })}
              />
            </AdvancedMarker>
          ))}
      </Map>
      {legendButtonVisible && (
        <div className="absolute right-[58px] top-[10px] z-[1000]">
          <button
            data-testid="map-info-button"
            className="flex items-center justify-center rounded-[2px] bg-white p-2.5 text-[#666] shadow-md transition-colors hover:bg-gray-100"
            style={{ boxShadow: '0 1px 4px -1px rgba(0,0,0,0.3)' }}
            onClick={() => setShowOverlay(true)}
          >
            <InformationCircleIcon className="size-5 text-black" />
          </button>
        </div>
      )}
      {showOverlay && (
        <MapLegendOverlay
          onClose={() => {
            setShowOverlay(false);
          }}
        />
      )}
    </div>
  );
};

WF_Map.displayName = 'WF_Map';
export default WF_Map;
